import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import "./AppShell.css";
import { Icon } from "../components/Icon";
import type { IconName } from "../components/Icon";
import { useTheme } from "../lib/theme";
import type { Theme } from "../lib/theme";
import { useChatDock } from "../chat/dock";
import { Bell } from "../components/Alarm";
import { AccountMenu } from "../components/Auth";
import { MyTurnButton } from "../components/MyTurn";
import { useMe } from "./AuthGate";
import { tx } from "../i18n";

/** 셸 — 공통 v3 의 상단 바(`.b-topnav`) + **메뉴별** 좌측 레일 + 폰 하단 탭바(`.b-tabbar`).
 *  어느 폭에서 무엇이 보이는지는 공통 CSS 가 정한다 — 959px 이하에서 상단 메뉴가 서랍(`.b-topnav-drawer`)으로
 *  접히고, 767px 이하(폰)에서 하단 탭바가 주 메뉴 넷 + 「더보기」를 든다. 화면 폭을 JS 로 재지 않는다.
 *  **레일은 하위 보기·필터가 둘 이상인 화면만 둔다**(M9 A안, 사람 답 q-faedf9ffeb92f26749f9951a) —
 *  설명 글만 담을 자리가 아니다. 설명은 본문 제목 아래 리드로 간다(docs/decisions/design-system.md).
 *  ≤959px 에서는 레일이 본문 **아래로** 내려간다 — 접어 숨기면 그 화면의 맥락 메뉴에 좁은 화면에서만
 *  닿을 수 없게 된다. */
/** `admin` 이 붙은 칸은 **관리자에게만 그린다** — 그 화면이 부르는 문이 전부 `/admin/` 아래라
 *  일반 사용자가 열면 403 만 보는 빈 화면이 된다. 그리는 것을 막는 것은 편의이고, 막는 것은
 *  서버의 관문이다(`docs/decisions/auth.md` §5).
 *  `group` 은 상단 바의 주 메뉴(t-in-port-shell) — b3x-pr 의 대시보드 · 일감 · 관측 · 관리 넷에 디자인 패턴을
 *  더한 다섯이다. 화면 하나하나는 그 묶음의 **왼쪽 레일**에 선다(매핑: docs/screens/pr-port.md 「셸」). */
export type NavGroup = "dash" | "work" | "obs" | "admin" | "patterns";
export const NAV: { href: string; label: string; admin?: boolean; icon?: IconName; group: NavGroup }[] = [
  { href: "#/", label: tx("nav.dashboard"), icon: "dashboard", group: "dash" },
  { href: "#/help", label: tx("nav.help"), icon: "help", group: "dash" },
  { href: "#/board", label: tx("nav.board"), icon: "kanban", group: "work" },
  { href: "#/queue", label: tx("nav.queue"), icon: "listChecks", group: "work" },
  { href: "#/graph", label: tx("nav.graph"), icon: "circleDot", group: "work" },
  { href: "#/milestones", label: tx("nav.milestones"), icon: "flag", group: "work" },
  { href: "#/projects", label: tx("nav.projects"), icon: "layers", group: "work" },
  { href: "#/session", label: tx("nav.session"), icon: "activity", group: "obs" },
  { href: "#/history", label: tx("nav.history"), icon: "circleCheck", group: "obs" },
  { href: "#/alarms", label: tx("nav.alarms"), icon: "bell", group: "obs" },
  { href: "#/tidy", label: tx("nav.tidy"), icon: "listChecks", group: "obs" },
  { href: "#/servers", label: tx("nav.servers"), icon: "server", group: "obs" },
  { href: "#/logs", label: tx("nav.logs"), admin: true, icon: "scroll", group: "obs" },
  { href: "#/settings", label: tx("nav.settings"), icon: "settings", group: "admin" },
  // 게시판은 관리 묶음이지만 관리자 전용이 아니다 — 남은 손일을 보는 것은 누구나다(docs/decisions/chores.md).
  { href: "#/chores", label: tx("nav.chores"), icon: "check", group: "admin" },
  // 조직도 관리자 전용이 아니다 — 멤버는 자기 조직을, OWNER 는 멤버를 여기서 본다(docs/decisions/org.md).
  { href: "#/orgs", label: tx("nav.orgs"), icon: "users", group: "admin" },
  { href: "#/conn", label: tx("nav.conn"), admin: true, icon: "plug", group: "admin" },
  { href: "#/git", label: tx("nav.git"), admin: true, icon: "gitBranch", group: "admin" },
  { href: "#/code", label: tx("nav.code"), admin: true, icon: "code", group: "admin" },
  // 패턴 페이지는 묶음 안에 숨기지 않는다 — 내비바에 상시 서 있는 검토 자리다(AGENTS.md 「디자인」).
  { href: "#/patterns", label: tx("nav.patterns"), icon: "palette", group: "patterns" },
];

/** 주 메뉴 다섯 — 순서가 곧 상단 바 순서다. 누르면 그 묶음의 **첫 화면**으로 간다(묶음 자체의 화면은 없다). */
export const GROUPS: { key: NavGroup; label: string; icon: IconName }[] = [
  { key: "dash", label: tx("nav.group.dash"), icon: "dashboard" },
  { key: "work", label: tx("nav.group.work"), icon: "kanban" },
  { key: "obs", label: tx("nav.group.obs"), icon: "activity" },
  { key: "admin", label: tx("nav.group.admin"), icon: "settings" },
  { key: "patterns", label: tx("nav.group.patterns"), icon: "palette" },
];

/** 지금 화면이 어느 묶음인가 — 메뉴에 없는 화면(내 계정 · 승인 창)은 null 이라 어느 것도 켜지 않는다. */
export function groupOf(route: string, nav: typeof NAV): NavGroup | null {
  return nav.find((n) => isOn(n.href, route))?.group ?? null;
}

/** 폰 하단 탭바에 싣는 넷 — 매일 여는 화면(대시보드 · 보드 · 세션 · 정리). 손가락 칸이라 넷 + 「더보기」까지만
 *  든다(공통 규칙). 나머지는 「더보기」가 여는 서랍에 있다. */
const TABBAR = ["#/", "#/board", "#/session", "#/tidy"];

const THEMES: { key: Theme; label: string; icon: "monitor" | "sun" | "moon" }[] = [
  { key: "system", label: tx("nav.theme.system"), icon: "monitor" },
  { key: "light", label: tx("nav.theme.light"), icon: "sun" },
  { key: "dark", label: tx("nav.theme.dark"), icon: "moon" },
];

/** 펼친 메뉴를 밖을 누르거나 Esc 로 닫는다 — 테마 · 더보기가 같이 쓴다. */
function useDismiss(open: boolean, close: () => void, wrap: RefObject<HTMLElement>) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close, wrap]);
}

/** 테마 — 아이콘 단추 하나 + 메뉴(t-in-shell-nav-overflow, 시안 A). 고른 것은 **단추의 아이콘 · 이름
 *  (aria-label · title)과 메뉴의 체크**로 말한다 — 색만 바뀌면 무엇을 고른 것인지 색을 못 보는 사람에게
 *  안 읽힌다. 메뉴 판은 공통 `.b-menu`. */
export function ThemeToggle({ forceOpen }: { forceOpen?: boolean } = {}) {
  const [theme, setTheme] = useTheme();
  const [open, setOpen] = useState(Boolean(forceOpen));
  const wrap = useRef<HTMLDivElement>(null);
  useDismiss(open && !forceOpen, () => setOpen(false), wrap);
  const cur = THEMES.find((t) => t.key === theme) ?? THEMES[0];
  return (
    <div className="b-menu-wrap" ref={wrap}>
      <button
        type="button"
        className="b-ibtn b-btn-ghost"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tx("nav.theme.current", { name: cur.label })}
        title={tx("nav.theme.current", { name: cur.label })}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name={cur.icon} />
      </button>
      {open ? (
        <div className="b-menu" role="menu" aria-label={tx("nav.theme.label")}>
          {THEMES.map((t) => (
            <button
              key={t.key}
              type="button"
              role="menuitemradio"
              aria-checked={theme === t.key}
              className="b-menu-item"
              onClick={() => {
                setTheme(t.key);
                setOpen(false);
              }}
            >
              <Icon name={t.icon} />
              <span>{t.label}</span>
              {theme === t.key ? <Icon name="check" className="navmenu__check" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function isOn(href: string, route: string): boolean {
  return href === `#${route}` || (route === "/" && href === "#/");
}

/** 내비바의 「채팅」 단추 — **어느 화면에서든** 겉창을 연다(t-in-chat-drawer). 열림은 눌림
 *  상태로, 도는 세션 수는 글자로 말한다. 수를 모르면(못 읽음) 수를 안 적는다 — 0 이 아니다. */
export function ChatButton() {
  const { open, running, toggleDock } = useChatDock();
  return (
    <button
      type="button"
      className={`b-btn b-btn-ghost navchat${open ? " is-on" : ""}`}
      aria-pressed={open}
      aria-label={running ? tx("nav.chat.labelRunning", { n: running }) : tx("nav.chat.label")}
      title={open ? tx("nav.chat.closeTip") : tx("nav.chat.openTip")}
      onClick={toggleDock}
    >
      <Icon name="chat" />
      <span className="navchat__text">{tx("nav.chat.button")}</span>
      {running ? <span className="b-badge b-badge-success navchat__count">{tx("nav.chat.running", { n: running })}</span> : null}
    </button>
  );
}

export function AppShell({
  route,
  sidebar,
  sidebarTitle,
  children,
}: {
  route: string;
  sidebar?: ReactNode;
  sidebarTitle?: string;
  children: ReactNode;
}) {
  const auth = useMe();
  const [drawer, setDrawer] = useState(false);
  const [railed, setRailed] = useRail();
  // 화면을 옮기면 서랍을 닫는다 — 열린 채로 다음 화면을 가리지 않게.
  useEffect(() => setDrawer(false), [route]);
  // 문지기 밖(로그인 전 화면은 셸을 안 쓴다)에서는 null — 그때는 칸을 거르지 않는다.
  const nav = NAV.filter((n) => !n.admin || !auth || auth.me.role === "admin");
  const tabs = TABBAR.map((h) => nav.find((n) => n.href === h)).filter((n): n is (typeof NAV)[number] => Boolean(n));
  // 화면이 하나도 안 보이는 묶음(관리자가 아닌 사람의 빈 묶음)은 주 메뉴에서 뺀다 — 눌러도 갈 곳이 없다.
  const groups = GROUPS.map((g) => {
    const items = nav.filter((n) => n.group === g.key);
    return { ...g, items, href: items[0]?.href ?? "#/" };
  }).filter((g) => g.items.length > 0);
  const cur = groupOf(route, nav);
  const curGroup = groups.find((g) => g.key === cur);
  // 묶음 레일 — 화면이 둘 이상인 묶음만 선다(하나뿐이면 갈 곳이 지금 화면뿐이라 레일이 할 말이 없다).
  const groupRail =
    curGroup && curGroup.items.length > 1 ? (
      <>
        <SideGroup>{curGroup.label}</SideGroup>
        {curGroup.items.map((n) => (
          <SideLink key={n.href} href={n.href} active={isOn(n.href, route)} icon={n.icon}>
            {n.label}
          </SideLink>
        ))}
      </>
    ) : null;
  const rail = groupRail || sidebar ? (
    <>
      {groupRail}
      {sidebar}
    </>
  ) : null;
  // 탭바에 없는 화면에 있으면 「더보기」가 켜져 있어야 지금 어디인지 보인다.
  const inTabbar = tabs.some((n) => isOn(n.href, route));
  return (
    <div className="shell b-has-tabbar">
      <header className="b-topnav">
        <div className="b-topnav-in">
          <a className="b-brand" href="#/">
            <span className="b-brand-mark" aria-hidden="true" />
            b3x-in
          </a>
          <nav className="b-topnav-links" aria-label={tx("nav.label")}>
            {groups.map((g) => (
              <a
                key={g.key}
                className={`b-navlink b-tip is-below is-center${g.key === cur ? " is-on" : ""}`}
                href={g.href}
                aria-current={g.key === cur ? "page" : undefined}
                data-tip={g.label}
              >
                <Icon name={g.icon} />
                <span className="b-navlink-label">{g.label}</span>
              </a>
            ))}
          </nav>
          <div className="b-topnav-actions">
            {auth ? <MyTurnButton me={auth.me.username} /> : null}
            <Bell />
            <ChatButton />
            <ThemeToggle />
            {auth ? <AccountMenu me={auth.me} onLogout={auth.logout} /> : null}
            <button
              type="button"
              className="b-ibtn b-btn-ghost b-topnav-menu"
              aria-expanded={drawer}
              aria-controls="b3x-drawer"
              aria-label={drawer ? tx("nav.closeMenu") : tx("nav.openMenu")}
              onClick={() => setDrawer((v) => !v)}
            >
              <Icon name={drawer ? "x" : "menu"} />
            </button>
          </div>
        </div>
        {drawer ? (
          <nav className="b-topnav-drawer" id="b3x-drawer" aria-label={tx("nav.labelAll")}>
            {groups.map((g) => (
              <div key={g.key} className="navdrawer__group">
                {g.items.length > 1 ? <span className="b-sidenav-group">{g.label}</span> : null}
                {g.items.map((n) => (
                  <a
                    key={n.href}
                    className={`b-navlink${isOn(n.href, route) ? " is-on" : ""}`}
                    href={n.href}
                    aria-current={isOn(n.href, route) ? "page" : undefined}
                  >
                    {n.icon ? <Icon name={n.icon} /> : null}
                    <span className="b-navlink-label">{n.label}</span>
                  </a>
                ))}
              </div>
            ))}
          </nav>
        ) : null}
      </header>

      <div className="body">
        {/* 레일 제목은 **안 그린다** — 본문 h1 과 같은 말을 두 번 하게 된다(A안). 이름은 보조기술에만. */}
        {rail ? (
          <aside
            className={`side b-sidenav${railed ? " is-collapsed" : ""}`}
            aria-label={sidebarTitle ?? tx("nav.side")}
          >
            <RailContext.Provider value={railed}>{rail}</RailContext.Provider>
            <button
              type="button"
              className="b-sidenav-item b-tip side__toggle"
              aria-expanded={!railed}
              aria-label={railed ? tx("nav.sideExpand") : tx("nav.sideCollapse")}
              data-tip={railed ? tx("nav.sideExpand") : ""}
              onClick={() => setRailed(!railed)}
            >
              <Icon name={railed ? "panelLeftOpen" : "panelLeftClose"} />
              <span className="b-sidenav-label">{railed ? tx("nav.sideExpand") : tx("nav.sideCollapse")}</span>
            </button>
          </aside>
        ) : null}
        <main className="b-page body__main">{children}</main>
      </div>

      <nav className="b-tabbar" aria-label={tx("nav.labelPhone")}>
        {tabs.map((n) => (
          <a
            key={n.href}
            className={`b-tabbar-item${isOn(n.href, route) ? " is-on" : ""}`}
            href={n.href}
            aria-current={isOn(n.href, route) ? "page" : undefined}
          >
            <span className="b-tabbar-icon">{n.icon ? <Icon name={n.icon} /> : null}</span>
            <span className="b-tabbar-label">{n.label}</span>
          </a>
        ))}
        <button
          type="button"
          className={`b-tabbar-item${!inTabbar || drawer ? " is-on" : ""}`}
          aria-expanded={drawer}
          aria-controls="b3x-drawer"
          onClick={() => {
            setDrawer((v) => !v);
            window.scrollTo({ top: 0 });
          }}
        >
          <span className="b-tabbar-icon">
            <Icon name="more" />
          </span>
          <span className="b-tabbar-label">{tx("common.action.more")}</span>
        </button>
      </nav>
    </div>
  );
}

/* ══ 접힌 레일 ══
   레일(아이콘만 남는 56px)은 **사람이 고른 것**이라 새로고침 · 화면 이동 뒤에도 남는다 — 브라우저 저장소의
   `b3x.side.railed` 한 칸. 기기마다 따로다(넓은 모니터와 노트북에서 다르게 쓰는 것이 보통이라 계정에 두지 않는다).
   저장소를 못 읽으면(사생활 창 등) 펼친 채로 시작하고 조용히 넘어간다 — 기본값이 곧 원래 모양이라 잃는 것이 없다.
   959px 이하에서는 레일이 본문 아래로 내려가므로 접힘을 안 쓴다(CSS 가 되돌린다). */
const RAIL_KEY = "b3x.side.railed";
const RailContext = createContext(false);

function useRail(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === "1";
    } catch {
      return false;
    }
  });
  return [
    on,
    (v) => {
      setOn(v);
      try {
        localStorage.setItem(RAIL_KEY, v ? "1" : "0");
      } catch {
        /* 저장 못 함 — 이번 화면에서만 접힌다. */
      }
    },
  ];
}

/** 접힌 레일 안의 줄로 그린다 — 패턴 페이지가 셸 밖에서 레일 모양을 보이려고 쓴다. 셸은 이것 없이 스스로 판다. */
export function SideRail({ children }: { children: ReactNode }) {
  return <RailContext.Provider value={true}>{children}</RailContext.Provider>;
}

/** 레일일 때만 툴팁 글자를 준다 — 펼친 메뉴에서는 이름이 이미 보이므로 빈 값(공통 .b-tip 규칙). */
function useTip(children: ReactNode, tip?: string): string {
  const railed = useContext(RailContext);
  if (!railed) return "";
  return tip ?? (typeof children === "string" ? children : "");
}

/* ══ 레일 부품 — 사이드바 안에 서는 것은 이 다섯뿐이다 ══
   줄 모양은 **한 벌** — 공통 v3 의 세로 메뉴 `.b-sidenav-item` 이고 링크 · 단추 · 체크가 그 위에 탄다. 화면이
   줄 CSS 를 따로 적으면(예전의 정리·알람 화면 전용 줄 클래스) 정렬과 선택 표시가 화면마다 갈린다. 선택은
   공통 모양 그대로(바탕 + 왼쪽 2px 띠)에 `aria-current` 를 함께 단다 — 색만으로 가르지 않는다. */

/** 줄 안쪽 — 이름은 왼쪽, 수는 오른쪽. 수가 `null`(못 셌다)이면 자리째 비운다 — 0 으로 그리면
 *  「셌더니 0」과 구분이 안 된다. */
function RowInner({ children, count, icon }: { children: ReactNode; count?: number | null; icon?: IconName }) {
  return (
    <>
      {icon ? <Icon name={icon} /> : null}
      <span className="b-sidenav-label side__text">{children}</span>
      {count === undefined || count === null ? null : <span className="side__count tnum">{count}</span>}
    </>
  );
}

/** 사이드바 줄 — 주소를 바꾸는 것. 높이는 공통 md(32). */
export function SideLink({
  href,
  children,
  active,
  count,
  icon,
  tip,
}: {
  href: string;
  children: ReactNode;
  active?: boolean;
  count?: number | null;
  /** 줄 앞 아이콘 — 접힌 레일에서는 이것만 남는다. */
  icon?: IconName;
  /** 레일 툴팁 글자. 이름이 글자 하나(string)면 생략해도 그것을 쓴다. */
  tip?: string;
}) {
  return (
    <a
      className={`b-sidenav-item b-tip${active ? " is-on" : ""}`}
      href={href}
      aria-current={active ? "true" : undefined}
      data-tip={useTip(children, tip)}
    >
      <RowInner count={count} icon={icon}>
        {children}
      </RowInner>
    </a>
  );
}

/** 같은 줄 모양인데 **주소를 안 바꾸는** 것(같은 화면 안에서 움직이는 메뉴·필터).
 *  링크처럼 보이는 button 이라야 키보드와 보조기술이 「눌리는 것」으로 읽는다. */
export function SideAction({
  children,
  onClick,
  active,
  count,
  disabled,
  icon,
  tip,
}: {
  children: ReactNode;
  onClick: () => void;
  active?: boolean;
  count?: number | null;
  disabled?: boolean;
  icon?: IconName;
  tip?: string;
}) {
  return (
    <button
      type="button"
      className={`b-sidenav-item b-tip${active ? " is-on" : ""}`}
      aria-current={active ? "true" : undefined}
      disabled={disabled}
      onClick={onClick}
      data-tip={useTip(children, tip)}
    >
      <RowInner count={count} icon={icon}>
        {children}
      </RowInner>
    </button>
  );
}

/** 체크 줄 — 여럿을 함께 켜는 필터. 줄 전체가 라벨이라 어디를 눌러도 켜진다. 선택 표시(바탕)는
 *  안 쓴다 — 켜진 것은 체크 표가 말하고, 바탕까지 칠하면 여럿이 동시에 「선택」으로 읽힌다. */
export function SideCheck({
  children,
  checked,
  onChange,
  count,
  tip,
}: {
  children: ReactNode;
  checked: boolean;
  onChange: (on: boolean) => void;
  count?: number | null;
  tip?: string;
}) {
  // 체크 줄의 「아이콘」은 체크 칸 자체다 — 레일에서도 켜짐이 칸에 그대로 보인다.
  return (
    <label className="b-sidenav-item b-tip side__check" data-tip={useTip(children, tip)}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <RowInner count={count}>{children}</RowInner>
    </label>
  );
}

/** 묶음 이름 — 공통 .b-sidenav-group(항목과 다른 색). 묶음이 하나뿐이어도 이름을 단다(무엇을 고르는 줄인지가
 *  거기 있다). 레일에서는 공통 CSS 가 글자를 걷고 얇은 선으로 묶음 경계만 남긴다. */
export function SideGroup({ children }: { children: ReactNode }) {
  return <span className="b-sidenav-group">{children}</span>;
}

/** 레일 바닥의 한 줄 사실(「지금 12건 보는 중」). 설명 글을 여기 두지 마라 — 설명은 본문 리드다. */
export function SideNote({ children }: { children: ReactNode }) {
  return <p className="caption side__note">{children}</p>;
}
